import type { OverdueInvoicesResult } from '../lib/dashboardApi';
import { filterByCompany, TEST_COMPANY_2_ID } from './companies';
import { isoDate } from './company';

// Overdue invoices fixture — 3 client groups, 2 matched to CRM (with email)
// and 1 unmatched (no clientId → no send-reminder button).

interface TaggedGroup {
  companyId: string | null;
  clientId: string | null;
  clientName: string;
  cif: string | null;
  email: string | null;
  emailCandidates: string[];
  invoiceCount: number;
  totalOverdue: number;
  currency: string;
  maxDaysOverdue: number;
  lastReminderAt: string | null;
  invoices: Array<{
    id: string;
    series: string;
    number: string;
    issueDate: string | null;
    dueDate: string;
    daysOverdue: number;
    totalAmount: number;
    paidAmount: number;
    outstanding: number;
    currency: string;
    paymentStatus: string;
  }>;
}

function buildGroups(): TaggedGroup[] {
  return [
    {
      companyId: null, // default firm
      clientId: 'c1',
      clientName: 'Acme Solutions SRL',
      cif: 'RO12345678',
      email: 'contact@acme-solutions.ro',
      emailCandidates: ['contact@acme-solutions.ro', 'facturare@acme-solutions.ro'],
      invoiceCount: 2,
      totalOverdue: 3500,
      currency: 'RON',
      maxDaysOverdue: 45,
      lastReminderAt: isoDate(-3),
      invoices: [
        {
          id: 'ov-inv-1',
          series: 'FCT',
          number: '0412',
          issueDate: isoDate(-75),
          dueDate: isoDate(-45),
          daysOverdue: 45,
          totalAmount: 2000,
          paidAmount: 0,
          outstanding: 2000,
          currency: 'RON',
          paymentStatus: 'UNPAID',
        },
        {
          id: 'ov-inv-2',
          series: 'FCT',
          number: '0425',
          issueDate: isoDate(-50),
          dueDate: isoDate(-20),
          daysOverdue: 20,
          totalAmount: 1500,
          paidAmount: 0,
          outstanding: 1500,
          currency: 'RON',
          paymentStatus: 'UNPAID',
        },
      ],
    },
    {
      companyId: TEST_COMPANY_2_ID, // secondary firm
      clientId: 'c5',
      clientName: 'Beta Construct SRL',
      cif: 'RO87654321',
      email: 'office@beta-construct.ro',
      emailCandidates: ['office@beta-construct.ro'],
      invoiceCount: 1,
      totalOverdue: 850.5,
      currency: 'RON',
      maxDaysOverdue: 12,
      lastReminderAt: null,
      invoices: [
        {
          id: 'ov-inv-3',
          series: 'PRF',
          number: '0089',
          issueDate: isoDate(-30),
          dueDate: isoDate(-12),
          daysOverdue: 12,
          totalAmount: 1200,
          paidAmount: 349.5,
          outstanding: 850.5,
          currency: 'RON',
          paymentStatus: 'PARTIAL',
        },
      ],
    },
    {
      companyId: null, // default firm, unmatched
      clientId: null,
      clientName: 'Partener Necunoscut SRL',
      cif: null,
      email: null,
      emailCandidates: [],
      invoiceCount: 1,
      totalOverdue: 450,
      currency: 'RON',
      maxDaysOverdue: 8,
      lastReminderAt: null,
      invoices: [
        {
          id: 'ov-inv-4',
          series: 'FCT',
          number: '0501',
          issueDate: isoDate(-25),
          dueDate: isoDate(-8),
          daysOverdue: 8,
          totalAmount: 450,
          paidAmount: 0,
          outstanding: 450,
          currency: 'RON',
          paymentStatus: 'UNPAID',
        },
      ],
    },
  ];
}

export function buildOverdueInvoices(companyId?: string | null): OverdueInvoicesResult {
  const all = buildGroups();
  const filtered = filterByCompany(all, companyId);
  const clients = filtered.map(({ companyId: _cid, ...rest }) => rest);
  const totalOverdue = clients.reduce((sum, g) => sum + g.totalOverdue, 0);
  return { clients, totalOverdue, clientCount: clients.length };
}
